import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import { DigestCardSkeleton } from './Skeleton';
import { stamp } from '../utils/age';
import toast from 'react-hot-toast';

interface Digest {
  id: string;
  type: 'personal' | 'instance';
  startDate: string;
  endDate: string;
  summary: string;
  metadata: string;
  createdAt: string;
}

interface DigestMetadata {
  reads?: number;
  likes?: number;
  shares?: number;
  comments?: number;
  starred?: number;
  articleCount?: number;
  totalReads?: number;
  totalLikes?: number;
  totalShares?: number;
  totalComments?: number;
  activeUsers?: number;
  topArticleCount?: number;
}

interface FirstDigestStatus {
  hasDigests: boolean;
  canGenerate: boolean;
  requirements: {
    feeds: { required: number; current: number; met: boolean };
    shares: { required: number; current: number; met: boolean };
    likes: { required: number; current: number; met: boolean };
  };
}

function dateRange(start: string, end: string): string {
  return `${format(new Date(start), 'MMM d')} – ${format(new Date(end), 'MMM d, yyyy')}`;
}

export default function DigestList() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token)!;
  const [digests, setDigests] = useState<Digest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const digestRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [firstDigestStatus, setFirstDigestStatus] = useState<FirstDigestStatus | null>(null);
  const [generatingFirstDigest, setGeneratingFirstDigest] = useState(false);

  useEffect(() => {
    fetchDigests();
    fetchFirstDigestStatus();
  }, []);

  async function fetchDigests() {
    try {
      const response = await fetch(`/api/digests?limit=20&offset=0`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch digests');

      const data = await response.json();
      setDigests(data.digests);
    } catch (error) {
      console.error('Error fetching digests:', error);
      toast.error('Failed to load digests');
    } finally {
      setLoading(false);
    }
  }

  async function fetchFirstDigestStatus() {
    try {
      const response = await fetch('/api/digests/first-digest-status', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch status');

      const data = await response.json();
      setFirstDigestStatus(data);
    } catch (error) {
      console.error('Error fetching first digest status:', error);
    }
  }

  async function handleGenerateFirstDigest() {
    if (!firstDigestStatus?.canGenerate) return;

    setGeneratingFirstDigest(true);
    try {
      const response = await fetch('/api/digests/generate-first', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate digest');
      }

      const data = await response.json();
      toast.success('Your first digest is ready');
      navigate(`/digest/${data.digest.id}`);
    } catch (error: any) {
      console.error('Error generating first digest:', error);
      toast.error(error.message || 'Failed to generate digest');
    } finally {
      setGeneratingFirstDigest(false);
    }
  }

  useEffect(() => {
    function handleKeyPress(e: KeyboardEvent) {
      if (document.activeElement?.tagName === 'INPUT' ||
          document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'j':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, digests.length - 1));
          break;
        case 'k':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < digests.length) {
            navigate(`/digest/${digests[selectedIndex].id}`);
          }
          break;
        case 'escape':
          e.preventDefault();
          setSelectedIndex(-1);
          break;
      }
    }

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [digests, selectedIndex, navigate]);

  useEffect(() => {
    if (selectedIndex >= 0 && selectedIndex < digestRefs.current.length) {
      digestRefs.current[selectedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (digestRefs.current.length !== digests.length) {
      digestRefs.current.length = digests.length;
    }
    setSelectedIndex(-1);
  }, [digests]);

  async function handleDelete(digestId: string, e: React.MouseEvent) {
    e.stopPropagation();

    if (!confirm('Delete this digest? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/digests/${digestId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete digest');

      toast.success('Digest deleted');
      fetchDigests();
    } catch (error) {
      console.error('Error deleting digest:', error);
      toast.error('Failed to delete digest');
    }
  }

  function parseMetadata(metadata: string): DigestMetadata {
    try {
      return JSON.parse(metadata);
    } catch {
      return {};
    }
  }

  function statsLine(digest: Digest): string {
    const m = parseMetadata(digest.metadata);
    const parts: string[] = [];
    if (digest.type === 'personal') {
      if (m.reads) parts.push(`${m.reads} read`);
      if (m.likes) parts.push(`${m.likes} liked`);
      if (m.shares) parts.push(`${m.shares} shared`);
    } else {
      if (m.totalReads) parts.push(`${m.totalReads} reads`);
      if (m.totalShares) parts.push(`${m.totalShares} shares`);
      if (m.activeUsers) parts.push(`${m.activeUsers} readers`);
    }
    return parts.join(' · ');
  }

  if (loading) {
    return (
      <div className="min-h-full bg-canvas">
        <div className="rail"><span className="rail-tab is-active">Digest</span></div>
        <DigestCardSkeleton />
        <DigestCardSkeleton />
        <DigestCardSkeleton />
      </div>
    );
  }

  const showFirstDigest = digests.length === 0 && firstDigestStatus && !firstDigestStatus.hasDigests;
  const requirementRows = firstDigestStatus ? [
    { label: 'Subscribe to feeds', req: firstDigestStatus.requirements.feeds },
    { label: 'Share articles', req: firstDigestStatus.requirements.shares },
    { label: 'Like an article', req: firstDigestStatus.requirements.likes },
  ] : [];

  return (
    <div className="min-h-full bg-canvas page-transition">
      <div className="rail sticky top-0 z-10 bg-canvas">
        <span className="rail-tab is-active">Digest</span>
        <span className="rail-tab is-quiet border-r-0">Sundays · what you read, what faded</span>
      </div>

      {showFirstDigest && (
        <div className="max-w-list">
          <div className="pl-4 lg:pl-6 pr-4 py-6 border-b border-edge">
            <div className="kicker mb-2">First digest</div>
            <h1 className="text-2xl mb-2">Your first digest is a few steps away.</h1>
            <p className="text-ink-secondary">
              Once you've done these, the digest arrives every Sunday at 6am.
            </p>
          </div>
          {requirementRows.map((row) => (
            <div key={row.label} className="grid grid-cols-[4.5rem_1fr] border-b border-edge-subtle">
              <div className={`pl-4 lg:pl-6 py-3 border-r border-edge-subtle stamp ${row.req.met ? 'text-pine' : ''}`}>
                {row.req.met ? 'DONE' : `${row.req.current}/${row.req.required}`}
              </div>
              <div className={`pl-4 py-3 text-[15px] ${row.req.met ? 'text-ink-muted line-through' : 'text-ink'}`}>
                {row.label} · {row.req.required}
              </div>
            </div>
          ))}
          <div className="pl-4 lg:pl-6 pr-4 py-5">
            <button
              onClick={handleGenerateFirstDigest}
              disabled={!firstDigestStatus.canGenerate || generatingFirstDigest}
              className="btn btn-accent"
            >
              {generatingFirstDigest ? 'Generating…' : firstDigestStatus.canGenerate ? 'Generate my first digest' : 'Finish the steps above'}
            </button>
          </div>
        </div>
      )}

      {digests.length === 0 && (firstDigestStatus === null || firstDigestStatus.hasDigests) && (
        <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
          <div className="kicker mb-1">Empty</div>
          <p className="text-ink-secondary">
            No digests yet. A quiet week with nothing read and nothing fading doesn't produce one.
          </p>
        </div>
      )}

      {digests.map((digest, index) => {
        const isSelected = selectedIndex === index;
        const stats = statsLine(digest);
        return (
          <div
            key={digest.id}
            ref={(el) => { digestRefs.current[index] = el; }}
            onClick={() => navigate(`/digest/${digest.id}`)}
            className={`grid grid-cols-[3.5rem_1fr] lg:grid-cols-[4.5rem_1fr] border-b border-edge-subtle border-l-2 cursor-pointer ${
              isSelected ? 'bg-surface-sunken border-l-accent' : 'border-l-transparent hover:bg-surface-sunken'
            }`}
          >
            <div className="pl-2 lg:pl-3 pr-2 py-3 border-r border-edge-subtle">
              <span className="stamp">{stamp(index)}</span>
            </div>
            <div className="min-w-0 pl-3 lg:pl-4 pr-4 py-2.5 max-w-list">
              <div className="flex items-start justify-between gap-3">
                <div className="kicker">
                  {digest.type === 'personal' ? 'Personal' : 'Instance'} · {dateRange(digest.startDate, digest.endDate)}
                  {stats && <> · {stats}</>}
                </div>
                <button
                  onClick={(e) => handleDelete(digest.id, e)}
                  className="p-1 -mt-1 text-ink-faint hover:text-signal"
                  title="Delete digest"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-[16px] text-ink leading-snug line-clamp-2">
                {digest.summary}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
